import { BarChart } from '@mantine/charts'
import { Text } from '@mantine/core'
import { ResponsiveContainer } from 'recharts'

import { colors } from '@/configs/colors'

import { ConversationTooltip } from './ConversationByDateTooltip'
import { NoDataAvailable } from './NoDataAvailable'

const conversationSeries = [
  {
    name: 'unique',
    label: 'Unique Conversations',
    color: colors.chartColors[1],
  },
  {
    name: 'total',
    label: 'Total Conversations',
    color: colors.chartColors[0],
  },
]

interface ConversationData {
  label: string
  taskId: string
  total: number
  unique: number
}

interface CMConversationBarChartProps {
  conversationData: ConversationData[]
  averageConversationLength: number
  title: string
}

export default function CMConversationBarChart({
  conversationData,
  averageConversationLength,
  title,
}: CMConversationBarChartProps) {
  // Use the conversation data as-is since parent has already filtered it
  const processedChartData = conversationData

  const chartHeight = Math.max(processedChartData.length * 40, 200)
  const hasConversationData = processedChartData.length > 0

  if (!hasConversationData) {
    return (
      <div>
        <Text size="sm" c="dimmed" mb="md">
          {title}
        </Text>
        <NoDataAvailable />
      </div>
    )
  }

  return (
    <div>
      <Text size="sm" fw={500} mb="md">
        {title}
      </Text>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={processedChartData.map(entry => ({
            ...entry,
            total: entry.total - entry.unique,
          }))}
          dataKey="label"
          type="stacked"
          orientation="vertical"
          series={conversationSeries}
          gridAxis="none"
          yAxisProps={{
            interval: 0,
            tickFormatter: (value: string, _: number) => {
              const maxChars = 24
              return value.length > maxChars ? value.substring(0, maxChars - 3) + '...' : value
            },
            width: 150,
            tick: {
              textAnchor: 'start',
              dx: -140,
              fontSize: 12,
            },
          }}
          tooltipProps={{
            content: ({ payload, label }) => {
              if (!payload || payload.length === 0) return null

              // Find the total value for this date point
              const totalEntry = payload.find(entry => entry.name === 'total')
              const uniqueEntry = payload.find(entry => entry.name === 'unique')
              const totalValue = (totalEntry?.value || 0) + (uniqueEntry?.value || 0)
              if (totalEntry) {
                totalEntry.value = totalValue
              }

              // Filter out the original 'total' entry and add the modified one
              const newPayload = [
                { ...totalEntry, value: totalValue },
                ...payload.filter(entry => entry.name !== 'total'),
              ]

              return <ConversationTooltip label={label} percentage={0} payload={newPayload} />
            },
          }}
          referenceLines={[
            {
              y: averageConversationLength,
              color: 'orange',
              label: `${Math.round(averageConversationLength)}`,
              strokeDasharray: '5 5',
              labelPosition: 'right',
            },
          ]}
        />
      </ResponsiveContainer>
    </div>
  )
}
